import {
  ComponentType,
  CSSProperties,
  ForwardedRef,
  forwardRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import { useDeviceStore } from '@namespace/device';
import { Box } from '../../../Box';
import { LazyLoadImage } from '../../../LazyLoadImage';
import { SLIDER_ITEM_TYPES } from '../../constants';
import { CarouselSkelet } from '../CarouselSkelet';
import { SnippetSlide } from './SnippetSlide';

import styles from '../../index.module.css';

interface SlideImage {
  lang?: string;
  imagePath?: string;
  imagePathDark?: string;
  imagesPerCurrency?: {
    currency: string;
    imagePath?: string;
    imagePathDark?: string;
  }[];
}

interface TabletSlideData {
  images?: SlideImage[];
  defaultImageLink?: string;
  defaultImageLinkDark?: string;
}

interface SlideData {
  source: string;
  url?: string;
  link?: string;
  images?: SlideImage[];
  defaultImageLink?: string;
  defaultImageLinkDark?: string;
  snippetId?: string;
  tablet?: TabletSlideData;
  id?: string;
  name?: string;
}

interface ExtendedMouseEvent extends MouseEvent {
  index?: number;
  slideId?: string;
  slideName?: string;
}

interface SlideProps {
  slide: SlideData;
  handleClickOnMove: (event: ExtendedMouseEvent) => void;
  home?: string;
  language?: string;
  LocalizedLink: ComponentType<{
    to: string;
    onClick?: (event: MouseEvent) => void;
    children: ReactNode;
    style?: CSSProperties | string;
    ref?: ForwardedRef<HTMLAnchorElement>;
    rel?: string;
    className?: string;
  }>;
  HTMLSnippet?: ComponentType<{
    snippetId: string;
  }>;
  currentCurrency?: string;
  className?: string;
  classNames?: {
    slideSkeleton?: string;
    slideImg?: string;
  };
  gap?: string;
  paddingBottom?: CSSProperties;
  getImgUrl: (image?: string, fallback?: string) => string;
  index?: number;
  draggable?: boolean;
}

export const Slide = forwardRef<HTMLAnchorElement, SlideProps>(
  (
    {
      slide,
      index,
      handleClickOnMove,
      home,
      language,
      LocalizedLink,
      HTMLSnippet,
      currentCurrency,
      className,
      classNames = {},
      gap,
      paddingBottom,
      getImgUrl,
      draggable
    },
    ref: ForwardedRef<HTMLAnchorElement>
  ) => {
    const {
      source,
      url,
      link,
      images = [],
      defaultImageLink,
      defaultImageLinkDark,
      snippetId,
      tablet = {}
    } = slide;
    const slideRef = useRef(null);
    const { isTablet } = useDeviceStore() as { isTablet: boolean };
    const [isLoaded, setIsLoaded] = useState<boolean>(
      typeof window !== 'undefined' && window.IS_SSR
    );
    const isLoadedImage = useCallback(() => setIsLoaded(true), []);

    useImperativeHandle(
      ref,
      () => slideRef.current || ({} as HTMLAnchorElement)
    );

    const handleClick = useCallback(
      (props: MouseEvent): void => {
        const extendedEvent: ExtendedMouseEvent = {
          ...props,
          index,
          slideId: slide?.id,
          slideName: slide?.name
        };
        handleClickOnMove(extendedEvent);
      },
      [handleClickOnMove, index, slide]
    );

    if (source === SLIDER_ITEM_TYPES.HTML_SNIPPET) {
      return (
        <SnippetSlide
          ref={slideRef}
          handleClickOnMove={handleClick}
          gap={gap || ''}
          snippetId={snippetId || ''}
          HTMLSnippet={HTMLSnippet as ComponentType<{ snippetId: string }>}
        />
      );
    }

    if (source === SLIDER_ITEM_TYPES.IMAGE) {
      const {
        images: tabletImages,
        defaultImageLink: tabletDefaultImageLink,
        defaultImageLinkDark: tabletDefaultImageLinkDark
      } = tablet;
      const isTabletImages = isTablet && !isEmpty(tablet);
      const slideImages = isTabletImages ? tabletImages : images;
      const slideDefaultImageLink =
        (isTabletImages && tabletDefaultImageLink) || defaultImageLink;
      const slideDefaultImageLinkDark =
        (isTabletImages && tabletDefaultImageLinkDark) || defaultImageLinkDark;

      const { imagePath, imagePathDark, imagesPerCurrency } =
        slideImages?.find(({ lang }) => lang === language) || {};

      const { imagePath: currencyImage, imagePathDark: currencyImageDark } =
        imagesPerCurrency?.find(
          ({ currency: curr }) => curr === currentCurrency
        ) || {};

      const imageForLanguageDark = imagePathDark || slideDefaultImageLinkDark;
      const imageForLanguage = getImgUrl(imagePath, imageForLanguageDark);

      const imageForCurrencyDark = currencyImageDark || imageForLanguageDark;
      const imageForCurrency = getImgUrl(currencyImage, imageForCurrencyDark);

      const defaultImage = getImgUrl(
        slideDefaultImageLink,
        slideDefaultImageLinkDark
      );

      const imgPath = imageForCurrency || imageForLanguage || defaultImage;
      const imgLink = url || link || '';

      return (
        <LocalizedLink
          style={gap && { marginRight: gap }}
          ref={slideRef}
          rel="noopener noreferrer"
          to={imgLink}
          onClick={handleClick}
          className={clsx([styles.link, className])}
          {...(draggable !== undefined && { draggable })}
        >
          <Box
            className={styles.lazyLoadWrapper}
            style={paddingBottom && { paddingBottom: `${paddingBottom}%` }}
          >
            <CarouselSkelet
              className={clsx([
                !isLoaded ? styles.show : styles.hide,
                classNames?.slideSkeleton
              ])}
            />
            <LazyLoadImage
              alt="Promotion material"
              src={imgPath}
              key={imgLink || home}
              width="100%"
              height="100%"
              className={clsx(
                styles.lazyLoadImg,
                isLoaded ? styles.show : styles.hide,
                classNames?.slideImg
              )}
              onLoad={isLoadedImage}
              {...(draggable !== undefined && { draggable })}
            />
          </Box>
        </LocalizedLink>
      );
    }

    return null;
  }
);
